import React, { useState } from 'react';
import { fmtCurrency, fmtNumber, fmtPercent } from '../../shared/format';
import {
  DimInsightCard,
  ExportMenu,
  INSIGHT_MIN_CONVERSIONS,
  downloadCsv,
  downloadPdf,
  makeExport,
} from '../shared';
import Pagination from '../../shared/Pagination';
import { SkeletonText } from '../../shared/Skeleton';
import { ErrorState } from '../../shared/StateMessage';
import ThinScroll from '../../shared/ThinScroll';
import InfoTip from '../../shared/InfoTip';
import { expandableRowProps } from '../../shared/expandableRow';

// Rows per page — 25, the Google Ads "work-through-the-list" table standard
// (matches NonConvertingTermsPanel / SearchTermExplorerPanel / the campaign
// drill-down). Named rather than inlined so it reads the same as its siblings.
//
// Exported because geo is SERVER-paginated: GoogleAdsPage passes this same limit
// to getGoogleAdsDimGeo, so the page size the RPC slices by and the size this
// panel renders its footer with cannot drift apart.
export const PER_PAGE = 25;

// Export spec — one column list drives CSV + PDF. CSV emits raw values + full
// headers; PDF emits formatted strings + abbreviated headers ("Conv. Value" → "Value").
const GEO_EXPORT_COLUMNS = [
  { header: 'Location', get: (geo) => geo.location },
  { header: 'Type', get: (geo) => geo.location_type || '' },
  { header: 'Impressions', pdfHeader: 'Impr.', get: (geo) => geo.impressions, pdf: (geo) => geo.impressions.toLocaleString() },
  { header: 'Clicks', get: (geo) => geo.clicks, pdf: (geo) => geo.clicks.toLocaleString() },
  { header: 'Cost', get: (geo) => geo.cost, pdf: (geo) => fmtCurrency(geo.cost) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (geo) => geo.conversions, pdf: (geo) => fmtNumber(geo.conversions, true) },
  { header: 'Conv. Value', pdfHeader: 'Value', get: (geo) => geo.conversion_value || 0, pdf: (geo) => fmtCurrency(geo.conversion_value) },
  { header: 'CPA', get: (geo) => geo.cpa ?? '', pdf: (geo) => (geo.cpa != null ? fmtCurrency(geo.cpa) : '—') },
  { header: 'CTR', get: (geo) => (geo.ctr * 100).toFixed(2) + '%', pdf: (geo) => fmtPercent(geo.ctr) },
];
const GEO_EXPORT = makeExport(GEO_EXPORT_COLUMNS);

const GeoPerformance = ({ geoData, loading, error, expanded, onToggle, page, onPageChange, rangeStart, rangeEnd }) => {
  const [openLocations, setOpenLocations] = useState({});
  const exportCsv = () => {
    if (!geoData?.items?.length) return;
    downloadCsv(GEO_EXPORT.headers, geoData.items.map(GEO_EXPORT.toCsvRow), `geographic-${rangeStart}-to-${rangeEnd}.csv`);
  };
  const exportPdf = () => {
    if (!geoData?.items?.length) return;
    downloadPdf('Geographic Performance', GEO_EXPORT.pdfHeaders, geoData.items.map(GEO_EXPORT.toPdfRow), `geographic-${rangeStart}-to-${rangeEnd}.pdf`, `${rangeStart} to ${rangeEnd}`);
  };

  const buildCustomInsight = () => {
    const items = geoData?.items;
    if (!items?.length) return null;
    const totalConv = items.reduce((sum, geo) => sum + geo.conversions, 0);
    const topSpend = items.slice(0, 3).map(geo => geo.geo_name || geo.location).join(', ');
    const bestCtr = items.filter(geo => geo.clicks >= 5).sort((geoA, geoB) => geoB.ctr - geoA.ctr)[0];
    const parts = [];
    parts.push(`Top spend locations: ${topSpend}`);
    if (totalConv >= INSIGHT_MIN_CONVERSIONS) {
      const bestConv = [...items].sort((geoA, geoB) => geoB.conversions - geoA.conversions)[0];
      if (bestConv && bestConv.conversions > 0) parts.push(`${bestConv.geo_name || bestConv.location} leads in conversions (${fmtNumber(bestConv.conversions, true)})`);
    }
    if (bestCtr) parts.push(`${bestCtr.geo_name || bestCtr.location} has the highest CTR at ${fmtPercent(bestCtr.ctr)} (min 5 clicks)`);
    return { parts, warningSuffix: 'conversion insights omitted' };
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center justify-between gap-2 px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="10" /><path d="M2 12h20" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
          </svg>
          <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">Geographic Performance</span>
          <InfoTip text="Spend, clicks, and conversions by city, state, or region. Helpful for spotting geo-targeting waste or expansion opportunities." />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-gray-400 dark:text-gray-500 font-medium">{expanded ? 'Collapse' : 'Expand'}</span>
          <svg className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t dark:border-gray-800">
          {error ? (
            <div className="pt-4"><ErrorState message={error} /></div>
          ) : loading ? (
            <div className="pt-4">
              <SkeletonText lines={8} />
            </div>
          ) : !geoData?.items?.length ? (
            <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">No geographic data available for this period.</p>
          ) : (
            <div className="pt-4">
              <div className="flex justify-end pb-2">
                <ExportMenu onCsv={exportCsv} onPdf={exportPdf} />
              </div>
              <ThinScroll className="responsive-card-table-wrapper overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-800">
                <table className="responsive-card-table w-full text-xs min-w-[760px]">
                  <thead>
                    <tr className="text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                      <th className="text-left py-2 px-3 font-medium">Location</th>
                      <th className="text-right py-2 px-3 font-medium">Impr.</th>
                      <th className="text-right py-2 px-3 font-medium">Clicks</th>
                      <th className="text-right py-2 px-3 font-medium">Cost</th>
                      <th className="text-right py-2 px-3 font-medium">Conv.</th>
                      <th className="text-right py-2 px-3 font-medium">Conv. Value</th>
                      <th className="text-right py-2 px-3 font-medium">CPA</th>
                      <th className="text-right py-2 px-3 font-medium">CTR</th>
                    </tr>
                  </thead>
                  <tbody>
                    {geoData.items.map((geo, index) => (
                      <tr
                        key={`${geo.location}-${index}`}
                        {...expandableRowProps({
                          expanded: !!openLocations[`${geo.location}-${index}`],
                          onToggle: () => setOpenLocations((prev) => ({ ...prev, [`${geo.location}-${index}`]: !prev[`${geo.location}-${index}`] })),
                          label: `metrics for ${geo.location}`,
                          className: `border-b dark:border-gray-800 last:border-0 max-[499px]:cursor-pointer ${openLocations[`${geo.location}-${index}`] ? '' : 'rc-collapsed'}`,
                        })}
                      >
                        <td data-label="Location" className={`rc-title-cell py-2.5 px-3 text-gray-900 dark:text-gray-100 font-medium ${openLocations[`${geo.location}-${index}`] ? 'rc-title-open' : ''}`}>
                          <span className="inline-flex items-center gap-2">
                            <svg className={`hidden max-[499px]:block w-3 h-3 text-gray-400 transition-transform ${openLocations[`${geo.location}-${index}`] ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                            </svg>
                            <span>
                              {geo.location}
                              {geo.location_type && (
                                <span className="ml-1.5 text-[12px] text-gray-400 dark:text-gray-500">({geo.location_type})</span>
                              )}
                            </span>
                          </span>
                        </td>
                        <td data-label="Impr." className="text-right px-3 text-gray-700 dark:text-gray-300">{geo.impressions.toLocaleString()}</td>
                        <td data-label="Clicks" className="text-right px-3 text-gray-700 dark:text-gray-300">{geo.clicks.toLocaleString()}</td>
                        <td data-label="Cost" className="text-right px-3 text-gray-700 dark:text-gray-300">{fmtCurrency(geo.cost)}</td>
                        <td data-label="Conv." className="text-right px-3 text-gray-700 dark:text-gray-300">{fmtNumber(geo.conversions, true)}</td>
                        <td data-label="Conv. Value" className="text-right px-3 text-gray-700 dark:text-gray-300">{fmtCurrency(geo.conversion_value)}</td>
                        <td data-label="CPA" className="text-right px-3 text-gray-700 dark:text-gray-300">{geo.cpa != null ? fmtCurrency(geo.cpa) : '—'}</td>
                        <td data-label="CTR" className="text-right px-3 text-gray-700 dark:text-gray-300">{fmtPercent(geo.ctr)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </ThinScroll>

              <Pagination
                page={page}
                totalPages={geoData.pagination?.total_pages ?? 1}
                onPageChange={onPageChange}
                totalItems={geoData.pagination?.total ?? geoData.pagination?.total_items ?? 0}
                perPage={PER_PAGE}
                label="locations"
              />

              <DimInsightCard
                insight={geoData.insight}
                totalClicks={geoData.items.reduce((sum, geo) => sum + geo.clicks, 0)}
                totalConv={geoData.items.reduce((sum, geo) => sum + geo.conversions, 0)}
                customParts={buildCustomInsight()}
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GeoPerformance;
